// Nachrichtenverlauf einer offenen Unterhaltung im iMessage-Stil: Tagestrenner, eigene Blasen
// blau rechts, andere grau links mit abgeflachter Ecke, in Gruppen Name über der ersten und
// Mini-Avatar an der letzten Blase einer Folge, „Zugestellt“ unter der letzten eigenen.
// Bekommt den fertig gefilterten Verlauf per Props — kein eigener useChat().
import { type RefObject } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { FileText, FolderSimplePlus, Paperclip, Trash } from '@phosphor-icons/react';
import { istBildDatei, kollegeName, tagesTrenner, zeitKurz, type ChatAnhang, type ChatNachricht, type Kollege } from '../../../../lib/chat/nachrichten';
import { blasenInfos } from '../../../../lib/island/chatDarstellung';
import { Avatar } from './Avatar';
import { ChatBild } from './ChatBild';

const QUELLE: Record<ChatAnhang['quelle'], string> = { drive: 'Dokument', email: 'Mail-Anhang', ticket: 'Ticket-Anhang' };

/** #549: Karte für Verweis oder Chat-Datei — Klick öffnet, optional „In Akte ablegen“. */
function DateiKarte({ name, unter, icon, onOeffnen, onAblegen }: {
  name: string; unter: string; icon: 'verweis' | 'datei'; onOeffnen: () => void; onAblegen?: () => void;
}) {
  const I = icon === 'verweis' ? Paperclip : FileText;
  return (
    <div className="flex items-center" style={{ gap: 8, minWidth: 200 }}>
      <button type="button" onClick={onOeffnen} title="Öffnen" className="flex min-w-0 flex-1 cursor-pointer items-center text-left"
        style={{ gap: 9, padding: '6px 8px', borderRadius: 12, border: 'none', background: 'rgba(0,0,0,0.18)', color: '#fff', fontFamily: 'inherit' }}>
        <span className="flex shrink-0 items-center justify-center" style={{ width: 30, height: 30, borderRadius: 9, background: 'rgba(255,255,255,0.16)' }}><I size={16} weight="fill" /></span>
        <span className="flex min-w-0 flex-col">
          <span className="truncate" style={{ fontSize: 13, fontWeight: 700 }}>{name}</span>
          <span className="truncate" style={{ fontSize: 11, opacity: 0.75 }}>{unter}</span>
        </span>
      </button>
      {onAblegen && (
        <button type="button" onClick={onAblegen} title="In Akte ablegen" aria-label="In Akte ablegen" className="flex shrink-0 cursor-pointer items-center justify-center"
          style={{ width: 32, height: 32, borderRadius: 10, border: 'none', background: 'rgba(0,0,0,0.18)', color: '#fff' }}>
          <FolderSimplePlus size={15} />
        </button>
      )}
    </div>
  );
}

export function Verlauf({ nachrichten, meineId, kollegen, zeigeAbsender, onLoeschen, onGross, onAnhang, onDatei, onAblegen, endeRef }: {
  nachrichten: ChatNachricht[];
  meineId: string | null;
  /** Nur für den Absendername im Gruppenverlauf gebraucht — bei 1:1 unbenutzt. */
  kollegen: Kollege[];
  /** Gruppenverlauf zeigt über fremden Nachrichten, von wem sie sind; 1:1 bleibt unverändert. */
  zeigeAbsender: boolean;
  onLoeschen: (n: ChatNachricht) => void;
  onGross: (b: { url: string; path: string; name: string | null }) => void;
  /** #549: Verweis öffnen */
  onAnhang: (a: ChatAnhang) => void;
  /** #549: im Chat hochgeladene Datei (kein Bild) öffnen bzw. in die Akte ablegen */
  onDatei: (path: string) => void;
  onAblegen: (path: string, name: string) => void;
  endeRef: RefObject<HTMLDivElement | null>;
}) {
  const ruhig = useReducedMotion() ?? false;
  const infos = blasenInfos(nachrichten, meineId);
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto" style={{ padding: '8px 12px', gap: 2 }}>
      {nachrichten.map((n, i) => {
        const info = infos[i]!;
        const eigen = n.absender_id === meineId;
        const absender = kollegen.find((k) => k.id === n.absender_id);
        return (
          <div key={n.id} style={{ marginTop: info.ersteDerFolge && i > 0 ? 10 : 0 }}>
            {info.neuerTag && (
              <div className="text-center" style={{ margin: '8px 0 10px', fontSize: 11, fontWeight: 700, color: 'rgba(235,235,245,0.45)' }}>
                {tagesTrenner(n.created_at)}
              </div>
            )}
            {zeigeAbsender && !eigen && info.ersteDerFolge && (
              <span className="block" style={{ margin: '0 0 2px 46px', fontSize: 11, fontWeight: 700, color: 'rgba(235,235,245,0.55)' }}>
                {kollegeName(absender)}
              </span>
            )}
            <div className={`group flex items-end ${eigen ? 'justify-end' : 'justify-start'}`} style={{ gap: 8 }}>
              {zeigeAbsender && !eigen && (
                <span style={{ width: 28, flexShrink: 0, opacity: info.letzteDerFolge ? 1 : 0 }}>
                  {info.letzteDerFolge && <Avatar name={kollegeName(absender)} bild={absender?.avatar_url} groesse={28} />}
                </span>
              )}
              {eigen && (
                <button
                  type="button"
                  onClick={() => onLoeschen(n)}
                  title="Nachricht löschen"
                  aria-label="Nachricht löschen"
                  className="shrink-0 cursor-pointer self-center opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100"
                  style={{ border: 'none', background: 'transparent', color: 'rgba(235,235,245,0.45)', padding: 4 }}
                >
                  <Trash size={13} />
                </button>
              )}
              <motion.div
                initial={ruhig ? false : { opacity: 0, y: 8, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ type: 'spring', stiffness: 420, damping: 26 }}
                style={{
                  maxWidth: '82%', padding: '9px 13px', fontSize: 14, lineHeight: 1.35,
                  borderRadius: eigen ? '20px 20px 6px 20px' : '20px 20px 20px 6px',
                  background: eigen ? 'linear-gradient(180deg,#2b9bff,#0a7aff)' : 'rgba(255,255,255,0.13)',
                  color: '#fff',
                }}
              >
                {n.bild_path && istBildDatei(n.bild_name || n.bild_path) && (
                  <div style={{ marginBottom: n.text ? 6 : 0 }}><ChatBild path={n.bild_path} name={n.bild_name} onGross={onGross} /></div>
                )}
                {n.bild_path && !istBildDatei(n.bild_name || n.bild_path) && (
                  <div style={{ marginBottom: n.text ? 6 : 0 }}>
                    <DateiKarte name={n.bild_name || 'Datei'} unter="Datei · wird nach 7 Tagen gelöscht" icon="datei"
                      onOeffnen={() => onDatei(n.bild_path!)} onAblegen={() => onAblegen(n.bild_path!, n.bild_name || 'Datei')} />
                  </div>
                )}
                {n.anhang && (
                  <div style={{ marginBottom: n.text ? 6 : 0 }}>
                    <DateiKarte name={n.anhang.name} unter={[QUELLE[n.anhang.quelle], n.anhang.kontext].filter(Boolean).join(' · ')} icon="verweis"
                      onOeffnen={() => onAnhang(n.anhang!)} />
                  </div>
                )}
                {n.text && <p className="whitespace-pre-wrap break-words" style={{ margin: 0 }}>{n.text}</p>}
                <span className="block text-right tabular-nums" style={{ marginTop: 2, fontSize: 10, color: eigen ? 'rgba(255,255,255,0.7)' : 'rgba(235,235,245,0.45)' }}>
                  {zeitKurz(n.created_at)}
                </span>
              </motion.div>
            </div>
            {info.zugestellt && (
              <span className="block text-right" style={{ marginTop: 3, fontSize: 10.5, fontWeight: 600, color: 'rgba(235,235,245,0.45)' }}>Zugestellt</span>
            )}
          </div>
        );
      })}
      <div ref={endeRef} />
    </div>
  );
}
